import { ReactNode, useEffect } from "react";
import { postMessage } from "../../../lib/post-message";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import {
  updateFailedCount,
  updateMessage,
  updateQueue,
} from "../../../redux/slices/pendingUploadsQueue";
import { replaceTempItemId, updatePhotoUrlInPoint } from "../../../redux/slices/workOrderDetailSlice";
import { setItemIdForRedirect } from "../../../redux/slices/redirectSlice";
import { showToaster } from "../../../redux/slices/toasterSlice";
import {
  isOfflineMode,
  onOfflineModeChange,
} from "../../../shared/offlineManager";

// Create a provider component
export function StatusProvider({ children }: { children: ReactNode }) {
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    const setMessage = (message: string) => dispatch(updateMessage(message));
    const setQueue = (queue: unknown[]) => {
      dispatch(updateQueue(queue));
    };
    const setFailedCount = (count: number) =>
      dispatch(updateFailedCount(count));

    postMessage({
      type: "SYNC_STATUS",
      payload: { message: "Hi from Status Provider" },
    });

    const handleServiceWorkerMessage = (event: MessageEvent) => {
      console.log(
        "[STATUS_PROVIDER]: Received message from service worker:",
        event?.data
      );
      const { type, payload } = event?.data || {};

      switch (type) {
        case "STATUS_UPDATE":
          setMessage(payload?.message || "");
          setQueue(payload?.items || []);
          break;

        case "FAILED_REQUEST":
          setFailedCount(payload?.items?.length || 0);
          break;

        case "ASSET_UPDATE":
          dispatch(
            replaceTempItemId({
              tempId: payload?.localId,
              realId: payload?.realId,
              workorder_id: payload?.workOrderId,
            })
          );
          dispatch(
            setItemIdForRedirect({
              itemIdForRedirect: payload?.realId,
              oldId: payload?.localId,
            })
          );
          break;

        case "STORAGE_LIMIT_ERROR":
          dispatch(
            showToaster({
              message: "Failed to add item, Storage limit reached!",
              type: "error",
            })
          );
          break;

        case "QUEUE_ERROR":
          dispatch(
            showToaster({
              message: "Failed to add item, Something unexpected happened!",
              type: "error",
            })
          );
          break;

        case "TOGGLE_OFFLINE_MODE":
          dispatch(
            showToaster({
              message: "Poor Connectivity! Switching to offline mode",
              type: "error",
            })
          );
          break;

        case "ERROR":
          dispatch(
            showToaster({
              message: "Error! " + payload.message,
              type: "error",
            })
          );
          break;

        case "INSPECTION_POINT_PHOTO_UPLOADED":
          // Update photo URL in Redux when service worker completes upload
          // Cleanup is handled by service worker after entire batch succeeds (deferred cleanup)
          if (
            payload?.workOrderId &&
            payload?.assetId &&
            payload?.sectionIndex !== undefined &&
            payload?.pointId &&
            payload?.uuid &&
            payload?.url
          ) {
            dispatch(
              updatePhotoUrlInPoint({
                workorder_id: payload.workOrderId,
                item_ID: payload.assetId,
                sectionIndex: payload.sectionIndex,
                pointId: payload.pointId,
                uuid: payload.uuid,
                url: payload.url,
              })
            );
          }
          break;

        default:
          break;
      }
    };

    navigator.serviceWorker.ready
      .then(() => {
        navigator.serviceWorker.addEventListener(
          "message",
          handleServiceWorkerMessage
        );
      })
      .catch((error) => {
        console.error(
          "Service worker not registered or failed to become ready:",
          error
        );
      });

    // ✅ OfflineMode Listener
    const unsubscribe = onOfflineModeChange((offline) => {
      if (!offline) {
        dispatch(
          showToaster({
            message: "Back online",
            type: "success",
          })
        );

        // Trigger sync queue when coming back online
        postMessage({
          type: "SYNC_QUEUE",
          payload: {
            message: "Sync queue after offline mode toggle",
          },
        });
      }
    });

    return () => {
      navigator.serviceWorker.removeEventListener(
        "message",
        handleServiceWorkerMessage
      );
      unsubscribe();
    };
  }, [dispatch]);

  return children;
}

// StatusBar component
import { useSyncExternalStore } from "react";

export function StatusBar({ isDarkMode }: { isDarkMode: boolean }) {
  const { message } = useSelector(
    (state: RootState) => state.pendingUploadsQueue
  );

  const isOffline = useSyncExternalStore(onOfflineModeChange, isOfflineMode);

  const brokenDownMessage = message.split(",");

  return (
    <>
      {isOffline && (
        <div className={`absolute px-2 rounded-full flex items-center justify-center space-x-2 shadow-md p-1 text-xs font-bold z-50 right-4 top-1 ${isDarkMode ? "bg-white bg-opacity-60 text-white" : "bg-black bg-opacity-10 text-black"}`}>
          <div className="w-2 h-2 rounded-full bg-red-400"></div>
          <span className="text-xs font-medium">Offline</span>
        </div>
      )}
      {message && (
        <div className="w-full p-1 bg-orange-100 text-center flex flex-col justify-center z-10 relative">
          {brokenDownMessage.map((m, index) => (
            <p key={index} className="text-center">
              {m}
            </p>
          ))}
        </div>
      )}
    </>
  );
}
